import { ReactElement, useEffect, useState } from 'react'

import { ToggleSwitch } from '@ui/components/core'
import { GetSettings, UpdateSettingQuery } from '@ui/loaders/settings'
import { useAuth } from '@ui/providers/AuthProvider'

interface Setting {
  name: string
  enabled: boolean
  last_updated_by: string
  last_updated_timestamp: string
}

export const Ingress = () => {
  const { currentMember } = useAuth()
  const [settingsState, setSettingsState] = useState<Setting[]>([])
  const { data, loading } = GetSettings()

  useEffect(() => {
    if (!loading && data?.getSettings) {
      setSettingsState(data.getSettings)
    }
  }, [data, loading])

  const [updateSetting] = UpdateSettingQuery()

  const updateSettingOnChange = async (name: string, enabled: boolean) => {
    // Apply setting update to database
    await updateSetting({
      variables: {
        setting: {
          name,
          enabled,
          last_updated_by: currentMember?.email,
        },
      },
    })

    // Update local state
    setSettingsState((prevState) => {
      return prevState.map((setting) => {
        if (setting.name === name) {
          return {
            ...setting,
            enabled,
          }
        }
        return setting
      })
    })
  }

  const getSettingEntries = () => {
    const settingElements: ReactElement[] = []
    const settings = [
      { name: 'new_relic_log_event_data_proxy_enabled', visibleText: 'New Relic - Proxy all log events' },
      { name: 'new_relic_span_event_data_proxy_enabled', visibleText: 'New Relic - Proxy all span events' },
      { name: 'new_relic_custom_event_data_proxy_enabled', visibleText: 'New Relic - Proxy all custom events' },
    ]
    for (const setting of settings) {
      const savedSetting = settingsState.find((settingFromState) => settingFromState.name === setting.name)
      settingElements.push(
        <tr key={setting.name}>
          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-normal text-gray-900 sm:pl-6">
            {setting.visibleText}
          </td>
          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-sm font-normal sm:pr-0">
            <div className={'ml-5'}>
              <ToggleSwitch
                checked={savedSetting?.enabled}
                onChange={() => updateSettingOnChange(setting.name, !savedSetting?.enabled)}
              />
            </div>
          </td>
        </tr>
      )
    }
    return settingElements
  }

  return (
    <>
      <div className="mt-5 flow-root h-full">
        <div className="-my-2 h-full w-full overflow-x-auto rounded-md">
          <div className="inline-block min-w-full align-middle">
            <div className="sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 backdrop-blur sm:pl-6">
                      Name
                    </th>
                    <th
                      scope="col"
                      className="sticky top-0 z-10 border-b border-gray-300 bg-white py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 backdrop-blur sm:pl-6">
                      Enabled
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {data && settingsState.length && !loading ? getSettingEntries() : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
